import { DAY_SLEEP, type DaySleep } from '@adhd/shared';
import { useState } from 'react';
import { api, type DayFace } from '../lib/api.js';

/**
 * Last night, in three words. Release 3 of 2.0 (docs/PRD-2.0.md §2).
 *
 * Words, never hours. Tapping the chosen one again clears it. "Short" also
 * sets the short-night day mark, so everything that reads marks sees the same
 * fact; the server does that, once, never as a toggle.
 *
 * Sleep from Health (14 Oct 2026): when the Shortcut has run, the word is
 * already chosen and the caption says so; a tap still changes it, and the
 * server keeps his tap over a later word from Health. The hours are never
 * shown — they are not even kept.
 */
const LABEL: Record<DaySleep, string> = { short: 'Short', okay: 'Okay', long: 'Long' };

export function SleepChips({ day, value, source, onChange }: { day: string; value: DaySleep | null; source: DayFace['sleepSource']; onChange: (face: DayFace) => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  async function pick(v: DaySleep): Promise<void> {
    const next = value === v ? null : v;
    setBusy(true);
    try {
      onChange(await api.setDaySleep(day, next));
    } catch {
      /* the chips stay as they were */
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="row" role="group" aria-label="Last night’s sleep" style={{ gap: 'var(--space-2)' }}>
      {DAY_SLEEP.map((v) => (
        <button
          key={v}
          type="button"
          className="chip"
          aria-pressed={value === v}
          disabled={busy}
          onClick={() => void pick(v)}
        >
          {LABEL[v]}
        </button>
      ))}
      <span className="faint">{source === 'health' && value ? 'From Health · tap to change' : 'Optional. Short also marks the day.'}</span>
    </div>
  );
}
